import * as React from 'react'

import { Box, Chevron, Popover, Text, useToggle } from '@factorialco/deprecated-design-system'

import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { minutesToText } from 'modules/core/lib/duration'
import i18n from 'modules/core/lib/i18n'

import type Store from 'modules/attendance/components/ClockIn/Store'
import TooltipHeader from 'modules/attendance/components/ClockIn/Tooltip/Header'
import TooltipInfoRow from 'modules/attendance/components/ClockIn/Tooltip/InfoRow'
import TooltipRow from 'modules/attendance/components/ClockIn/Tooltip/Row'
import MinutesToText from 'modules/attendance/components/MinutesToText'
import { BalanceBreakdown } from 'modules/attendance/hooks/useFetchAttendanceBalanceBreakdown'

type SizeType = React.ComponentProps<typeof Text>['size']
type WeightType = React.ComponentProps<typeof Text>['weight']

export interface Props {
  breakdown: BalanceBreakdown
  size?: SizeType
  weight?: WeightType
  children?: React.ReactNode
  store?: Store
}

interface RowProps {
  breakdown: BalanceBreakdown
}

const PreviousBalanceRow = ({ breakdown }: RowProps) => {
  return (
    <TooltipRow
      label={i18n.t('clock_in.balance_breakdown.previous_balance')}
      value={breakdown.poolPreviousBalance(breakdown.balancePool)}
      plusSign
      descriptions={[
        i18n.t('clock_in.balance_breakdown.previous_balance_since', {
          month: breakdown.month,
          year: breakdown.year,
        }),
      ]}
    />
  )
}

const BalanceRow = ({ breakdown }: RowProps) => {
  const enabled = breakdown.isPoolEnabled(breakdown.balancePool)
  const unlimited = breakdown.isPoolUnlimited(breakdown.balancePool)
  const capacity = minutesToText(breakdown.poolCapacity(breakdown.balancePool))
  const descriptions: string[] = []

  if (enabled && !unlimited)
    descriptions.push(i18n.t('clock_in.balance_breakdown.balance_info', { capacity }))

  return (
    <Box gap="s8">
      {breakdown.accumulationEnabled && <PreviousBalanceRow breakdown={breakdown} />}
      <TooltipRow
        label={i18n.t('clock_in.balance_breakdown.balance')}
        value={breakdown.rawBalance}
        plusSign
        descriptions={descriptions}
      />
    </Box>
  )
}

const CompensationRow = ({ breakdown }: RowProps) => {
  return (
    <Box paddingY="s16" gap="s4">
      <TooltipHeader small title={i18n.t('clock_in.balance_breakdown.compensated_overtime')} />
      <Box>
        {breakdown.payrollBreakdown && <PayrollRow breakdown={breakdown} />}
        {breakdown.timeoffBreakdown && <TimeoffRow breakdown={breakdown} />}
      </Box>
    </Box>
  )
}

const TimeoffRow = ({ breakdown }: RowProps) => {
  const unlimited = breakdown.isPoolUnlimited(breakdown.timeoffPool)
  const capacity = minutesToText(breakdown.poolCapacity(breakdown.timeoffPool))
  const notComputed =
    breakdown.isCurrentPeriod && breakdown.isPoolMonthlyUpdated(breakdown.timeoffPool)

  // Initially, we used negative transfers to move balances from one pool to another.
  // Now, we add usages instead. This new approach modifies the transfers from the API, so we need to correct it for the time-off pool.
  // Because this tooltip will soon be removed, we decided to subtract the discarded amount as a quick fix.
  const timeoffPreviousTransfers =
    breakdown.poolPreviousTransfers(breakdown.timeoffPool) -
    breakdown.poolPreviousTransfers(breakdown.discardedPool)

  const previous = minutesToText(timeoffPreviousTransfers)

  const descriptions: string[] = []
  if (!unlimited)
    descriptions.push(i18n.t('clock_in.balance_breakdown.timeoff_compensation_info', { capacity }))
  if (breakdown.accumulationEnabled)
    descriptions.push(
      i18n.t('clock_in.balance_breakdown.timeoff_previous_compensation', { previous })
    )

  // Initially, we used negative transfers to move balances from one pool to another.
  // Now, we add usages instead. This new approach modifies the transfers from the API, so we need to correct it for the time-off pool.
  // Because this tooltip will soon be removed, we decided to subtract the discarded amount as a quick fix.
  const timeoffPoolTransfers =
    breakdown.poolTransfers(breakdown.timeoffPool) -
    breakdown.poolTransfers(breakdown.discardedPool)

  return (
    <>
      <TooltipRow
        label={i18n.t('clock_in.balance_breakdown.timeoff_compensation')}
        value={-timeoffPoolTransfers}
        fallbackValue={notComputed}
        plusSign
        descriptions={descriptions}
      />
      {notComputed && (
        <TooltipInfoRow
          label={i18n.t('clock_in.balance_breakdown.timeoff_compensation_monthly_disclaimer')}
        />
      )}
    </>
  )
}

const PayrollRow = ({ breakdown }: RowProps) => {
  return (
    <TooltipRow
      label={i18n.t('clock_in.balance_breakdown.payroll_compensation')}
      value={-breakdown.poolTransfers(breakdown.payrollPool)}
      plusSign
      descriptions={[
        i18n.t('clock_in.balance_breakdown.payroll_available_to_compensate', {
          available: breakdown.availablePayrollCompensation,
        }),
      ]}
    />
  )
}

const DiscardedRow = ({ breakdown }: RowProps) => {
  const notComputed =
    breakdown.isCurrentPeriod && breakdown.isPoolMonthlyUpdated(breakdown.balancePool)

  return (
    <TooltipRow
      label={i18n.t('clock_in.balance_breakdown.discarded')}
      value={-breakdown.poolTransfers(breakdown.discardedPool)}
      fallbackValue={notComputed}
      plusSign
      descriptions={[i18n.t('clock_in.balance_breakdown.discarded_info')]}
    />
  )
}

export const BalanceBreakdownTooltip = ({ breakdown, size, weight, children, store }: Props) => {
  const [open, toggle] = useToggle(false)
  const isSeeingOwnPeriod = store?.isSeeingOwnPeriod() ?? false

  useEffect(() => {
    if (open) {
      track(trackingEvents.timeTracking.seeBalanceBreakdownTooltip, {
        page: store ? 'clock_in' : 'time_tracking',
        user_checking_own_timesheet: isSeeingOwnPeriod,
        date_from: breakdown.startAt,
        date_to: breakdown.endAt,
      })
    }
  }, [open])

  return (
    <>
      <Popover.ItemList attachment="bottom center" toggle={toggle} open={open} gapDivider={false}>
        <Box inline flexDirection="row" gap="s8" alignItems="center">
          <MinutesToText value={breakdown.balance} size={size} weight={weight} withPlusSign />
          <Chevron direction={open ? 'up' : 'down'} type="terciary" />
        </Box>
        <TooltipHeader
          title={i18n.t('clock_in.balance_breakdown.title')}
          subtitle={i18n.t(
            'clock_in.balance_breakdown.accumulation_time_balance_from_to',
            breakdown.duration
          )}
          minutes={breakdown.balance}
        />
        <BalanceRow breakdown={breakdown} />
        {breakdown.compensationBreakdown && <CompensationRow breakdown={breakdown} />}
        {breakdown.discardedBreakdown && <DiscardedRow breakdown={breakdown} />}
      </Popover.ItemList>
      {children}
    </>
  )
}
